import React, { useState } from 'react';

import { Text } from '~/components/core';
import SimpleSearchField from '~/components/core/SimpleSearchField';
import { ChevronDownIcon, ChevronUpIcon } from '~/components/icons';
import cn from '~/Utils/cn';

import type { ConfigurationScope, ConfigurationScopeGroup } from './scopes';
import { countCustomizedScopes, filterScopeGroups } from './scopes';
import { CONFIGURATIONS_TEXTS as TEXTS } from './texts';

type ScopeRowProps = {
  scope: ConfigurationScope;
  label: string;
  isSelected: boolean;
  onSelect: (key: string) => void;
  /** A scope outside the user's restriction is listed greyed out and cannot be opened. */
  isOpenable: boolean;
  indent?: boolean;
};

const ScopeRow = ({ scope, label, isSelected, onSelect, isOpenable, indent = false }: ScopeRowProps) => (
  <button
    type="button"
    aria-current={isSelected || undefined}
    disabled={!isOpenable}
    onClick={() => onSelect(scope.key)}
    className={cn(
      'flex w-full items-center justify-between gap-8 rounded-md border-none bg-transparent px-12 py-8 text-left',
      {
        'ps-24': indent,
        'cursor-pointer hover:bg-slate-100': isOpenable,
        'cursor-default': !isOpenable,
        'bg-teal-100 hover:bg-teal-100': isSelected,
      }
    )}
  >
    <Text
      variant={Text.VARIANTS.SM}
      colorVariant={
        !isOpenable
          ? Text.COLOR_VARIANTS.DISABLED
          : isSelected
            ? Text.COLOR_VARIANTS.BUTTON_LINK
            : Text.COLOR_VARIANTS.PRIMARY
      }
    >
      {label}
    </Text>
  </button>
);

type ConfigurationsRailProps = {
  defaultScope: ConfigurationScope;
  groups: ConfigurationScopeGroup[];
  hasSubOrganizations: boolean;
  selectedKey: string;
  onSelect: (key: string) => void;
  isOpenable: (scope: ConfigurationScope) => boolean;
};

const ConfigurationsRail = ({
  defaultScope,
  groups,
  hasSubOrganizations,
  selectedKey,
  onSelect,
  isOpenable,
}: ConfigurationsRailProps) => {
  const [query, setQuery] = useState('');
  const [foldedLobs, setFoldedLobs] = useState<ReadonlySet<string>>(new Set());
  const { customized, total } = countCustomizedScopes(groups, isOpenable);
  const shownGroups = filterScopeGroups(groups, query);
  const isSearching = query.trim() !== '';

  const toggleFold = (lob: string) =>
    setFoldedLobs((folded) => {
      const next = new Set(folded);
      if (next.has(lob)) {
        next.delete(lob);
      } else {
        next.add(lob);
      }
      return next;
    });

  return (
    <div className="flex h-full flex-col gap-4 pe-4" data-testid="configurations-rail">
      <div className="flex items-baseline justify-between px-12 pb-4">
        <Text weight={Text.WEIGHTS.SEMI_BOLD}>{TEXTS.railTitle}</Text>
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.customizedCount(customized, total)}
        </Text>
      </div>
      {hasSubOrganizations ? (
        <SimpleSearchField onChange={setQuery} placeholder={TEXTS.searchPlaceholder} renderClearButton />
      ) : null}
      <ScopeRow
        scope={defaultScope}
        label={TEXTS.defaultLabel}
        isSelected={selectedKey === defaultScope.key}
        onSelect={onSelect}
        isOpenable={isOpenable(defaultScope)}
      />
      {hasSubOrganizations
        ? shownGroups.map((group) => {
            // A search shows every match, so it does not leave one folded away.
            const isFolded = !isSearching && foldedLobs.has(group.lob);
            return (
              <div key={group.lob} className="flex flex-col">
                <button
                  type="button"
                  aria-expanded={!isFolded}
                  onClick={() => toggleFold(group.lob)}
                  className="flex w-full cursor-pointer items-center justify-between border-none bg-transparent px-12 pb-4 pt-12 text-left"
                >
                  <Text
                    variant={Text.VARIANTS.XS}
                    weight={Text.WEIGHTS.SEMI_BOLD}
                    colorVariant={Text.COLOR_VARIANTS.SECONDARY}
                    className="uppercase tracking-[0.06em]"
                  >
                    {group.label}
                  </Text>
                  {isFolded ? <ChevronDownIcon size={14} /> : <ChevronUpIcon size={14} />}
                </button>
                {isFolded
                  ? null
                  : group.subOrganizations.map((scope) => (
                      <ScopeRow
                        key={scope.key}
                        scope={scope}
                        label={scope.label}
                        isSelected={selectedKey === scope.key}
                        onSelect={onSelect}
                        isOpenable={isOpenable(scope)}
                        indent
                      />
                    ))}
              </div>
            );
          })
        : groups.map((group) => (
            // An organization without sub-organizations can still hold configurations made while it had them;
            // they are listed under their LOB so none drops out of sight or out of step with the count.
            <React.Fragment key={group.lob}>
              {group.lobOnly ? (
                <ScopeRow
                  scope={group.lobOnly}
                  label={group.lobOnly.label}
                  isSelected={selectedKey === group.lobOnly.key}
                  onSelect={onSelect}
                  isOpenable={isOpenable(group.lobOnly)}
                />
              ) : null}
              {group.subOrganizations.map((scope) => (
                <ScopeRow
                  key={scope.key}
                  scope={scope}
                  label={scope.label}
                  isSelected={selectedKey === scope.key}
                  onSelect={onSelect}
                  isOpenable={isOpenable(scope)}
                  indent
                />
              ))}
            </React.Fragment>
          ))}
      {isSearching && shownGroups.length === 0 ? (
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="px-12 py-8">
          {TEXTS.noSearchMatch}
        </Text>
      ) : null}
    </div>
  );
};

export default ConfigurationsRail;
